import { supabase } from "@/integrations/supabase/client";
import { 
  DutyRosterGroup, 
  GroupOffDays, 
  HolidayOverride, 
  DutyRosterAssignment,
  DutyRosterValidationResult 
} from '@/types';
import { 
  DutyRosterResidencyType,
  DutyRosterShiftPattern 
} from '@/types/enums';
import { 
  calculateWorkingDays,
  calculateOffDays,
  formatMonthYear,
  parseMonthYear,
  findNextWorkingDay,
  validateDutyRosterGroup,
  validateGroupOffDays,
  validateHolidayOverride,
  generateDutyRosterAssignments,
  canWorkOvertime,
  getEmployeeShiftForDate,
  isWorkingDay,
  isOffDay,
  validateEmployeeGroupResidency,
  assignEmployeeToGroup,
  bulkAssignEmployeesToGroup,
  removeEmployeeFromGroup,
  getEmployeesByDutyRosterStatus,
  getDutyRosterEmployeesWithGroups,
  validateEmployeeDutyRosterAssignment,
  isDutyRosterEmployee,
  isEmployeeAssignedToGroup,
  getEmployeeGroupInfo,
  filterEmployeesByDutyRosterStatus,
  filterEmployeesByGroup,
  getUngroupedDutyRosterEmployees
} from '@/utils/dutyRosterUtils';
import { normalizeResidencyForDB } from '@/utils/residencyUtils';

/**
 * Fetch group schedule data for OT validation
 * Returns working days, off days, and holiday overrides for a specific group and month
 */
export async function fetchGroupScheduleForOT(
  groupId: string, 
  monthYear: string
): Promise<{
  workingDays: number[];
  offDays: number[];
  holidayOverrides: HolidayOverride[];
  groupName: string;
  residencyType: string;
}> {
  try {
    // Fetch group details
    const { data: group, error: groupError } = await supabase
      .from('emp_groups')
      .select('group_name, residency_type')
      .eq('id', groupId)
      .eq('is_active', true)
      .single();

    if (groupError) throw groupError;
    if (!group) throw new Error('Group not found');

    // Fetch working days from shift_group table
    const { data: shiftGroups, error: shiftError } = await supabase
      .from('shift_group')
      .select('working_days')
      .eq('group_id', groupId)
      .eq('month_year', monthYear)
      .eq('is_active', true);

    if (shiftError) throw shiftError;

    // Fetch off days from group_off_days table
    const { data: offDaysData, error: offDaysError } = await supabase
      .from('group_off_days')
      .select('off_days')
      .eq('group_id', groupId)
      .eq('month_year', monthYear)
      .eq('is_active', true);

    if (offDaysError) throw offDaysError;

    // Fetch holiday overrides for this group and month
    const { data: holidayOverrides, error: holidayError } = await supabase
      .from('holiday_override')
      .select('*')
      .eq('group_id', groupId)
      .eq('month_year', monthYear)
      .eq('is_active', true);

    if (holidayError) throw holidayError;

    // Extract working days (flatten all working days from all shifts)
    const workingDays = shiftGroups?.reduce((acc: number[], shift) => {
      if (shift.working_days && Array.isArray(shift.working_days)) {
        acc.push(...shift.working_days);
      }
      return acc;
    }, []) || [];

    // Extract off days
    const offDays = offDaysData?.reduce((acc: number[], offDay) => {
      if (offDay.off_days && Array.isArray(offDay.off_days)) {
        acc.push(...offDay.off_days);
      }
      return acc;
    }, []) || [];

    return {
      workingDays: [...new Set(workingDays)], // Remove duplicates
      offDays: [...new Set(offDays)], // Remove duplicates
      holidayOverrides: holidayOverrides || [],
      groupName: group.group_name,
      residencyType: group.residency_type
    };
  } catch (error) {
    console.error('Error fetching group schedule for OT:', error);
    throw error;
  }
}

/**
 * Optimized validation for OT date for duty roster employee
 * Uses single query with joins to reduce database calls from 4+ to 1
 * This is the performance-optimized version
 */
export async function validateOTDateForDutyRosterOptimized(
  employeeId: string,
  otDate: string
): Promise<{
  isValid: boolean;
  isWorkingDay: boolean;
  isOffDay: boolean;
  isHoliday: boolean;
  isHolidayOverride: boolean;
  currentShift?: {
    id: string;
    name: string;
    code: string;
  };
  availableShifts?: any[];
  message?: string;
  groupName?: string;
}> {
  try {
    // Parse the OT date
    const otDateObj = new Date(otDate);
    const monthYear = `${otDateObj.getFullYear()}-${String(otDateObj.getMonth() + 1).padStart(2, '0')}`;
    const dayOfMonth = otDateObj.getDate();

    // Single optimized query with joins - replaces 4+ separate queries
    const { data: employeeData, error: empError } = await supabase
      .from('employees')
      .select(`
        id,
        group_id,
        emp_groups!inner(
          group_name,
          residency_type,
          shift_group(
            working_days,
            shift_id,
            shifts(name, code)
          ),
          group_off_days(off_days),
          holiday_override(
            org_off_day,
            off_day_shifted_to,
            is_active
          )
        )
      `)
      .eq('id', employeeId)
      .eq('is_duty_roster', true)
      .eq('emp_groups.is_active', true)
      .eq('emp_groups.shift_group.month_year', monthYear)
      .eq('emp_groups.shift_group.is_active', true)
      .eq('emp_groups.group_off_days.month_year', monthYear)
      .eq('emp_groups.group_off_days.is_active', true)
      .eq('emp_groups.holiday_override.month_year', monthYear)
      .eq('emp_groups.holiday_override.is_active', true)
      .single();

    if (empError) throw empError;
    if (!employeeData?.emp_groups) {
      throw new Error('Employee not assigned to any duty roster group');
    }

    const group = employeeData.emp_groups as any; // Type assertion for complex Supabase join
    const groupName = group.group_name || 'Unknown Group';

    // Extract working days from shift_group data
    const workingDays = group.shift_group?.reduce((acc: number[], shift: any) => {
      if (shift.working_days && Array.isArray(shift.working_days)) {
        acc.push(...shift.working_days);
      }
      return acc;
    }, []) || [];

    // Extract off days
    const offDays = group.group_off_days?.reduce((acc: number[], offDay: any) => {
      if (offDay.off_days && Array.isArray(offDay.off_days)) {
        acc.push(...offDay.off_days);
      }
      return acc;
    }, []) || [];

    // Check for holiday override - check if the selected date matches off_day_shifted_to
    const holidayOverride = group.holiday_override?.find((ho: any) => 
      new Date(ho.off_day_shifted_to).getDate() === dayOfMonth
    );

    // Determine day status
    const isWorkingDay = workingDays.includes(dayOfMonth);
    const isOffDay = offDays.includes(dayOfMonth);
    const isHolidayOverride = !!holidayOverride;

    // Determine the status and fetch additional data if needed
    let isHoliday = false;
    let message = '';
    let currentShift = undefined;
    let availableShifts = undefined;

    if (isHolidayOverride) {
      isHoliday = true;
      message = `Extended holiday - no overtime constraints. You can select any shift for overtime.`;
    } else if (isOffDay) {
      message = `Off day - no overtime constraints. You can select any shift for overtime.`;
    } else if (isWorkingDay) {
      // Find current shift for this working day from the joined data
      const currentShiftGroup = group.shift_group?.find((sg: any) => 
        sg.working_days && sg.working_days.includes(dayOfMonth)
      );

      if (currentShiftGroup?.shifts) {
        currentShift = {
          id: currentShiftGroup.shift_id,
          name: currentShiftGroup.shifts.name,
          code: currentShiftGroup.shifts.code || ''
        };
      }

      // Fetch available shifts (in_working_hours = TRUE, excluding current shift)
      const { data: shifts, error: shiftsError } = await supabase
        .from('shifts')
        .select('id, name, code')
        .eq('in_working_hours', true)
        .eq('is_active', true)
        .neq('id', currentShift?.id || '');

      if (!shiftsError && shifts) {
        availableShifts = shifts;
      }

      message = `Working day - select a different shift for overtime.`;
    } else {
      message = `Date not found in group schedule.`;
    }

    return {
      isValid: true,
      isWorkingDay,
      isOffDay,
      isHoliday,
      isHolidayOverride,
      currentShift,
      availableShifts,
      message,
      groupName
    };
  } catch (error) {
    console.error('Error validating OT date for duty roster:', error);
    return {
      isValid: false,
      isWorkingDay: false,
      isOffDay: false,
      isHoliday: false,
      isHolidayOverride: false,
      message: `Error validating date: ${error instanceof Error ? error.message : 'Unknown error'}`,
      groupName: undefined
    };
  }
}

/**
 * Validate OT date for duty roster employee
 * Returns validation result with shift constraints or off day message
 */
export async function validateOTDateForDutyRoster(
  employeeId: string,
  otDate: string
): Promise<{
  isValid: boolean;
  isWorkingDay: boolean;
  isOffDay: boolean;
  isHoliday: boolean;
  isHolidayOverride: boolean;
  currentShift?: {
    id: string;
    name: string;
    code: string;
  };
  availableShifts?: any[];
  message?: string;
  groupName?: string;
}> {
  try {
    // Get employee's group
    const { data: employee, error: empError } = await supabase
      .from('employees')
      .select('group_id')
      .eq('id', employeeId)
      .eq('is_duty_roster', true)
      .single();

    if (empError) throw empError;
    if (!employee?.group_id) {
      throw new Error('Employee not assigned to any duty roster group');
    }

    // Parse the OT date
    const otDateObj = new Date(otDate);
    const monthYear = `${otDateObj.getFullYear()}-${String(otDateObj.getMonth() + 1).padStart(2, '0')}`;
    const dayOfMonth = otDateObj.getDate();

    // Fetch group schedule
    const schedule = await fetchGroupScheduleForOT(employee.group_id, monthYear);

    // Check if it's a holiday override - check if the selected date matches off_day_shifted_to
    const holidayOverride = schedule.holidayOverrides.find(ho => 
      new Date(ho.off_day_shifted_to).getDate() === dayOfMonth
    );

    // Check if it's a working day
    const isWorkingDay = schedule.workingDays.includes(dayOfMonth);
    const isOffDay = schedule.offDays.includes(dayOfMonth);
    const isHolidayOverride = !!holidayOverride;

    // Determine the status
    let isHoliday = false;
    let message = '';
    let currentShift = undefined;
    let availableShifts = undefined;

    if (isHolidayOverride) {
      isHoliday = true;
      message = `Extended holiday - no overtime constraints. You can select any shift for overtime.`;
    } else if (isOffDay) {
      message = `Off day - no overtime constraints. You can select any shift for overtime.`;
    } else if (isWorkingDay) {
      // Fetch current shift for this working day
      const { data: shiftGroup, error: shiftError } = await supabase
        .from('shift_group')
        .select('shift_id, shifts(name, code)')
        .eq('group_id', employee.group_id)
        .eq('month_year', monthYear)
        .eq('is_active', true)
        .contains('working_days', [dayOfMonth])
        .single();

      if (!shiftError && shiftGroup && shiftGroup.shifts) {
        currentShift = {
          id: shiftGroup.shift_id,
          name: (shiftGroup.shifts as any).name,
          code: (shiftGroup.shifts as any).code || ''
        };
      }

      // Fetch available shifts (in_working_hours = TRUE, excluding current shift)
      const { data: shifts, error: shiftsError } = await supabase
        .from('shifts')
        .select('id, name, code')
        .eq('in_working_hours', true)
        .eq('is_active', true)
        .neq('id', currentShift?.id || '');

      if (!shiftsError && shifts) {
        availableShifts = shifts;
      }

      message = `Working day - select a different shift for overtime.`;
    } else {
      message = `Date not found in group schedule.`;
    }

    return {
      isValid: true,
      isWorkingDay,
      isOffDay,
      isHoliday,
      isHolidayOverride,
      currentShift,
      availableShifts,
      message,
      groupName: schedule.groupName
    };
  } catch (error) {
    console.error('Error validating OT date for duty roster:', error);
    return {
      isValid: false,
      isWorkingDay: false,
      isOffDay: false,
      isHoliday: false,
      isHolidayOverride: false,
      message: `Error validating date: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Service for managing duty roster operations
 */
export class DutyRosterService {
  /**
   * Create a new duty roster group
   */
  static async createGroup(groupData: Partial<DutyRosterGroup>): Promise<DutyRosterGroup> {
    try {
      const validation = validateDutyRosterGroup(groupData);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      // Normalize residency type to database format (lowercase with underscore)
      const normalizedResidency = normalizeResidencyForDB(groupData.residency_type || '');

      const { data, error } = await supabase
        .from('emp_groups')
        .insert({
          group_name: groupData.group_name,
          company_id: groupData.company_id,
          residency_type: normalizedResidency,
          is_active: groupData.is_active ?? true
        })
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to create group: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error creating duty roster group:', error);
      throw error;
    }
  }

  /**
   * Fetch all duty roster groups for a company
   */
  static async fetchGroups(companyId: string): Promise<DutyRosterGroup[]> {
    try {
      const { data, error } = await supabase
        .from('emp_groups')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('group_name', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch groups: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching duty roster groups:', error);
      throw error;
    }
  }

  /**
   * Update a duty roster group
   */
  static async updateGroup(groupId: string, updates: Partial<DutyRosterGroup>): Promise<DutyRosterGroup> {
    try {
      const validation = validateDutyRosterGroup(updates, true);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      // Normalize residency type to database format if it's being updated
      const normalizedUpdates = { ...updates };
      if (normalizedUpdates.residency_type) {
        normalizedUpdates.residency_type = normalizeResidencyForDB(normalizedUpdates.residency_type);
      }

      const { data, error } = await supabase
        .from('emp_groups')
        .update({
          ...normalizedUpdates,
          updated_at: new Date().toISOString()
        })
        .eq('id', groupId)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update group: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error updating duty roster group:', error);
      throw error;
    }
  }

  /**
   * Delete a duty roster group (soft delete)
   */
  static async deleteGroup(groupId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('emp_groups')
        .update({ is_active: false })
        .eq('id', groupId);

      if (error) {
        throw new Error(`Failed to delete group: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting duty roster group:', error);
      throw error;
    }
  }

  /**
   * Generate monthly roster for a group
   */
  static async generateMonthlyRoster(
    groupId: string,
    year: number,
    month: number,
    shiftId: string
  ): Promise<{ workingDays: number[]; offDays: number[] }> {
    try {
      const monthYear = formatMonthYear(year, month);
      
      // Get group details to determine residency type
      const { data: group, error: groupError } = await supabase
        .from('emp_groups')
        .select('residency_type')
        .eq('id', groupId)
        .single();
      
      if (groupError || !group) {
        throw new Error(`Failed to fetch group details: ${groupError?.message || 'Group not found'}`);
      }
      
      // Calculate working and off days based on residency type
      const workingDays = await calculateWorkingDays(year, month, group.residency_type as DutyRosterResidencyType);
      const offDays = await calculateOffDays(year, month, group.residency_type as DutyRosterResidencyType);

      // Store group off days
      const { error: offDaysError } = await supabase
        .from('group_off_days')
        .upsert({
          month_year: monthYear,
          group_id: groupId,
          off_days: offDays,
          is_active: true
        });

      if (offDaysError) {
        throw new Error(`Failed to store off days: ${offDaysError.message}`);
      }

      return { workingDays, offDays };
    } catch (error) {
      console.error('Error generating monthly roster:', error);
      throw error;
    }
  }

  /**
   * Assign employee to duty roster group
   */
  static async assignEmployeeToGroup(employeeId: string, groupId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('employees')
        .update({
          group_id: groupId,
          is_duty_roster: true
        })
        .eq('id', employeeId);

      if (error) {
        throw new Error(`Failed to assign employee to group: ${error.message}`);
      }
    } catch (error) {
      console.error('Error assigning employee to group:', error);
      throw error;
    }
  }

  /**
   * Remove employee from duty roster group
   */
  static async removeEmployeeFromGroup(employeeId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('employees')
        .update({
          group_id: null,
          is_duty_roster: false
        })
        .eq('id', employeeId);

      if (error) {
        throw new Error(`Failed to remove employee from group: ${error.message}`);
      }
    } catch (error) {
      console.error('Error removing employee from group:', error);
      throw error;
    }
  }

  /**
   * Fetch employees in a duty roster group
   */
  static async fetchGroupEmployees(groupId: string): Promise<Array<{
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    department: string;
    position: string;
    status: string;
    group_id: string;
    is_duty_roster: boolean;
  }>> {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select(`
          id,
          first_name,
          last_name,
          email,
          department,
          position,
          status,
          group_id,
          is_duty_roster
        `)
        .eq('group_id', groupId)
        .eq('is_duty_roster', true);

      if (error) {
        throw new Error(`Failed to fetch group employees: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching group employees:', error);
      throw error;
    }
  }

  /**
   * Fetch group off days for a specific month
   */
  static async fetchGroupOffDays(groupId: string, monthYear: string): Promise<GroupOffDays | null> {
    try {
      const { data, error } = await supabase
        .from('group_off_days')
        .select('*')
        .eq('group_id', groupId)
        .eq('month_year', monthYear)
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw new Error(`Failed to fetch group off days: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error fetching group off days:', error);
      throw error;
    }
  }

  /**
   * Fetch holiday overrides for a group
   */
  static async fetchHolidayOverrides(groupId: string, monthYear: string): Promise<HolidayOverride[]> {
    try {
      const { data, error } = await supabase
        .from('holiday_override')
        .select(`
          *,
          holidays:collision_with (
            id,
            name,
            date
          )
        `)
        .eq('group_id', groupId)
        .eq('month_year', monthYear)
        .eq('is_active', true)
        .order('org_off_day', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch holiday overrides: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching holiday overrides:', error);
      throw error;
    }
  }

  /**
   * Get working days for a group (R1.md requirement)
   * Updated to consider holiday overrides
   */
  static async getWorkingDays(groupId: string, monthYear: string): Promise<{ working_days: number[] }[]> {
    try {
      // Parse month-year
      const { year, month } = parseMonthYear(monthYear);

      // Get actual working days considering holiday overrides
      const { data: workingDays, error } = await supabase.rpc('get_actual_working_days_for_group', {
        p_group_id: groupId,
        p_year: year,
        p_month: month
      });

      if (error) {
        throw new Error(`Failed to get working days: ${error.message}`);
      }

      // Return in R1.md expected format
      return [{
        working_days: workingDays || []
      }];
    } catch (error) {
      console.error('Error getting working days:', error);
      throw error;
    }
  }

  /**
   * Get off days for a group (R1.md requirement)
   * Updated to consider holiday overrides
   */
  static async getOffDays(groupId: string, monthYear: string): Promise<{ off_days: number[] } | null> {
    try {
      // Parse month-year
      const { year, month } = parseMonthYear(monthYear);

      // Get actual off days considering holiday overrides
      const { data: offDays, error } = await supabase.rpc('get_actual_off_days_for_group', {
        p_group_id: groupId,
        p_year: year,
        p_month: month
      });

      if (error) {
        throw new Error(`Failed to get off days: ${error.message}`);
      }

      // Return in R1.md expected format
      return {
        off_days: offDays || []
      };
    } catch (error) {
      console.error('Error getting off days:', error);
      throw error;
    }
  }

  // Note: Holiday collision detection methods removed
  // Holiday collisions are now handled proactively by database triggers
  // The detect_all_holiday_collisions trigger automatically processes all groups
  // when holidays are added/updated/deleted

  /**
   * Validate overtime request against duty roster
   * Updated to include holiday collision detection (R1.md Scenario 5)
   */
  static async validateOvertimeRequest(
    employeeId: string,
    date: string,
    shiftId: string,
    companyId?: string
  ): Promise<DutyRosterValidationResult> {
    try {
      // Check if employee is on duty roster
      const { data: employee, error: employeeError } = await supabase
        .from('employees')
        .select('is_duty_roster, group_id')
        .eq('id', employeeId)
        .single();

      if (employeeError) {
        throw new Error(`Failed to fetch employee: ${employeeError.message}`);
      }

      if (!employee.is_duty_roster || !employee.group_id) {
        return {
          isValid: true, // Not on duty roster, use standard validation
          errors: [],
          warnings: []
        };
      }

      // Get group details to determine residency type
      const { data: group, error: groupError } = await supabase
        .from('emp_groups')
        .select('residency_type')
        .eq('id', employee.group_id)
        .single();

      if (groupError || !group) {
        return {
          isValid: false,
          errors: ['Failed to fetch group details'],
          warnings: []
        };
      }

      // Parse date to get year, month, day
      const dateObj = new Date(date);
      const year = dateObj.getFullYear();
      const month = dateObj.getMonth() + 1;
      const day = dateObj.getDate();

      // Use our RPC function to check if it's a working day (considering holiday overrides)
      // Holiday collisions are now handled proactively by database triggers
      const isWorking = await isWorkingDay(employee.group_id, year, month, day);
      const isOffDay = !isWorking;

      // For off days, allow OT (holiday collisions are handled proactively)
      if (isOffDay) {
        return {
          isValid: true, // Can work OT on off days
          errors: [],
          warnings: []
        };
      }

      // Check if requesting OT in assigned working shift
      const { data: shift, error: shiftError } = await supabase
        .from('shifts')
        .select('in_working_hours')
        .eq('id', shiftId)
        .single();

      if (shiftError) {
        throw new Error(`Failed to fetch shift: ${shiftError.message}`);
      }

      // For working days, only allow OT in non-working hours shifts
      if (shift.in_working_hours) {
        return {
          isValid: false,
          errors: ['Cannot work overtime in assigned working hours shift'],
          warnings: []
        };
      }

      return {
        isValid: true,
        errors: [],
        warnings: []
      };

    } catch (error) {
      console.error('Error validating overtime request:', error);
      return {
        isValid: false,
        errors: [`Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`],
        warnings: []
      };
    }
  }

  /**
   * Save working days to shift_group table
   */
  static async saveWorkingDays(
    groupId: string,
    shiftId: string,
    workingDays: number[],
    monthYear: string
  ): Promise<void> {
    try {
      // Validate input
      if (!groupId || !shiftId || !monthYear) {
        throw new Error('Group ID, Shift ID, and Month Year are required');
      }

      // If no working days, delete the entry instead of saving empty array
      if (workingDays.length === 0) {
        const { error } = await supabase
          .from('shift_group')
          .delete()
          .eq('group_id', groupId)
          .eq('month_year', monthYear)
          .eq('shift_id', shiftId);
        
        if (error) {
          throw new Error(`Database error: ${error.message}`);
        }
        return;
      }

      const { error } = await supabase
        .from('shift_group')
        .upsert({
          month_year: monthYear,
          group_id: groupId,
          shift_id: shiftId,
          working_days: workingDays,
          is_active: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'month_year,group_id,shift_id'
        });

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }

    } catch (error) {
      console.error('Error saving working days:', error);
      throw error;
    }
  }

  /**
   * Save off days to group_off_days table
   */
  static async saveOffDays(
    groupId: string,
    offDays: number[],
    monthYear: string
  ): Promise<void> {
    try {
      // Validate input
      if (!groupId || !monthYear) {
        throw new Error('Group ID and Month Year are required');
      }

      if (offDays.length === 0) {
        throw new Error('Off days array cannot be empty');
      }

      const { error } = await supabase
        .from('group_off_days')
        .upsert({
          month_year: monthYear,
          group_id: groupId,
          off_days: offDays,
          is_active: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'month_year,group_id'
        });

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }

    } catch (error) {
      console.error('Error saving off days:', error);
      throw error;
    }
  }

  /**
   * Fetch existing working days for a group and month
   */
  static async fetchWorkingDays(
    groupId: string,
    monthYear: string
  ): Promise<Array<{ shiftId: string; workingDays: number[] }>> {
    try {
      if (!groupId || !monthYear) {
        throw new Error('Group ID and Month Year are required');
      }

      const { data, error } = await supabase
        .from('shift_group')
        .select('shift_id, working_days')
        .eq('group_id', groupId)
        .eq('month_year', monthYear)
        .eq('is_active', true);

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }

      return data?.map(item => ({
        shiftId: item.shift_id,
        workingDays: item.working_days || []
      })) || [];

    } catch (error) {
      console.error('Error fetching working days:', error);
      throw error;
    }
  }

  /**
   * Delete all shift entries for a group and month
   */
  static async deleteAllShiftEntries(
    groupId: string,
    monthYear: string
  ): Promise<void> {
    try {
      if (!groupId || !monthYear) {
        throw new Error('Group ID and Month Year are required');
      }

      const { error } = await supabase
        .from('shift_group')
        .delete()
        .eq('group_id', groupId)
        .eq('month_year', monthYear);

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }

    } catch (error) {
      console.error('Error deleting shift entries:', error);
      throw error;
    }
  }

  /**
   * Fetch existing off days for a group and month
   */
  static async fetchOffDays(
    groupId: string,
    monthYear: string
  ): Promise<number[]> {
    try {
      if (!groupId || !monthYear) {
        throw new Error('Group ID and Month Year are required');
      }

      const { data, error } = await supabase
        .from('group_off_days')
        .select('off_days')
        .eq('group_id', groupId)
        .eq('month_year', monthYear)
        .eq('is_active', true);

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }

      // Return off_days from first row if exists, otherwise empty array
      return data && data.length > 0 ? data[0].off_days : [];

    } catch (error) {
      console.error('Error fetching off days:', error);
      throw error;
    }
  }

  /**
   * Fetch complete schedule for a group and month
   */
  static async fetchGroupSchedule(
    groupId: string,
    monthYear: string
  ): Promise<{
    workingDays: Array<{ shiftId: string; workingDays: number[] }>;
    offDays: number[];
  }> {
    try {
      const [workingDays, offDays] = await Promise.all([
        this.fetchWorkingDays(groupId, monthYear),
        this.fetchOffDays(groupId, monthYear)
      ]);

      return {
        workingDays,
        offDays
      };

    } catch (error) {
      console.error('Error fetching group schedule:', error);
      throw error;
    }
  }

  /**
   * Save complete group schedule (working days + off days)
   */
  static async saveGroupSchedule(
    groupId: string,
    shiftId: string,
    workingDays: number[],
    offDays: number[],
    monthYear: string
  ): Promise<void> {
    try {
      // Validate input
      if (!groupId || !monthYear) {
        throw new Error('Group ID and Month Year are required');
      }

      if (workingDays.length === 0 && offDays.length === 0) {
        throw new Error('At least one working day or off day must be provided');
      }

      const promises = [];

      // Save working days if provided
      if (workingDays.length > 0) {
        if (!shiftId) {
          throw new Error('Shift ID is required when assigning working days');
        }
        promises.push(this.saveWorkingDays(groupId, shiftId, workingDays, monthYear));
      }

      // Save off days if provided
      if (offDays.length > 0) {
        promises.push(this.saveOffDays(groupId, offDays, monthYear));
      }

      // Execute all operations
      await Promise.all(promises);

    } catch (error) {
      console.error('Error saving group schedule:', error);
      throw error;
    }
  }

  // NEW: Employee Management Methods

  /**
   * Validate employee-group residency compatibility
   */
  static async validateEmployeeGroupResidency(
    employeeId: string,
    groupId: string
  ): Promise<{
    isValid: boolean;
    employeeResidency: string;
    groupResidency: string;
    employeeName: string;
    groupName: string;
    message: string;
  }> {
    return await validateEmployeeGroupResidency(employeeId, groupId);
  }

  /**
   * Assign employee to duty roster group with validation
   */
  static async assignEmployeeToGroupWithValidation(
    employeeId: string,
    groupId: string,
    assignedBy?: string
  ): Promise<{
    success: boolean;
    employeeId: string;
    employeeName: string;
    groupId: string;
    groupName: string;
    message: string;
  }> {
    return await assignEmployeeToGroup(employeeId, groupId, assignedBy);
  }

  /**
   * Bulk assign multiple employees to a group
   */
  static async bulkAssignEmployeesToGroupWithValidation(
    employeeIds: string[],
    groupId: string,
    assignedBy?: string
  ): Promise<{
    totalProcessed: number;
    successfulAssignments: number;
    failedAssignments: number;
    successfulDetails: any[];
    failedDetails: Array<{ employeeId: string; error: string }>;
    message: string;
  }> {
    return await bulkAssignEmployeesToGroup(employeeIds, groupId, assignedBy);
  }

  /**
   * Remove employee from duty roster group
   */
  static async removeEmployeeFromGroupWithValidation(
    employeeId: string,
    removedBy?: string
  ): Promise<{
    success: boolean;
    employeeId: string;
    employeeName: string;
    removedGroupId?: string;
    removedGroupName?: string;
    message: string;
  }> {
    return await removeEmployeeFromGroup(employeeId, removedBy);
  }

  /**
   * Get employees by duty roster status
   */
  static async getEmployeesByDutyRosterStatus(
    companyId: string,
    isDutyRoster?: boolean,
    includeInactive: boolean = false
  ): Promise<{
    employees: any[];
    summary: {
      totalCount: number;
      dutyRosterCount: number;
      nonDutyRosterCount: number;
      filterApplied: boolean;
      includeInactive: boolean;
    };
  }> {
    return await getEmployeesByDutyRosterStatus(companyId, isDutyRoster, includeInactive);
  }

  /**
   * Get duty roster employees with their group information
   */
  static async getDutyRosterEmployeesWithGroups(
    companyId: string,
    groupId?: string
  ): Promise<{
    employees: any[];
    groups: any[];
    summary: {
      totalEmployees: number;
      groupedEmployees: number;
      ungroupedEmployees: number;
      totalGroups: number;
    };
  }> {
    return await getDutyRosterEmployeesWithGroups(companyId, groupId);
  }

  /**
   * Validate employee duty roster assignment
   */
  static async validateEmployeeDutyRosterAssignment(
    employeeId: string
  ): Promise<{
    isValid: boolean;
    employeeId: string;
    employeeName: string;
    isDutyRoster: boolean;
    groupId?: string;
    groupName?: string;
    validationErrors: string[];
    warnings: string[];
    message: string;
  }> {
    return await validateEmployeeDutyRosterAssignment(employeeId);
  }

  /**
   * Check if employee is duty roster employee
   */
  static isDutyRosterEmployee(employee: any): boolean {
    return isDutyRosterEmployee(employee);
  }

  /**
   * Check if employee is assigned to a group
   */
  static isEmployeeAssignedToGroup(employee: any): boolean {
    return isEmployeeAssignedToGroup(employee);
  }

  /**
   * Get employee's group information
   */
  static getEmployeeGroupInfo(employee: any): {
    groupId?: string;
    groupName?: string;
    groupResidencyType?: string;
  } {
    return getEmployeeGroupInfo(employee);
  }

  /**
   * Filter employees by duty roster status
   */
  static filterEmployeesByDutyRosterStatus(
    employees: any[],
    isDutyRoster?: boolean
  ): any[] {
    return filterEmployeesByDutyRosterStatus(employees, isDutyRoster);
  }

  /**
   * Filter employees by group
   */
  static filterEmployeesByGroup(
    employees: any[],
    groupId?: string
  ): any[] {
    return filterEmployeesByGroup(employees, groupId);
  }

  /**
   * Get employees without group assignment
   */
  static getUngroupedDutyRosterEmployees(employees: any[]): any[] {
    return getUngroupedDutyRosterEmployees(employees);
  }
}
